import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { Loader2, FileText } from 'lucide-react';

// Render the PDF worker from a bundled, same-origin asset (CSP: worker-src 'self').
// This renders PDFs inside the app instead of relying on the browser's built-in
// viewer, so previews work regardless of Chrome's "Download PDFs" setting.
pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

function PdfPage({ pdf, pageNumber, containerWidth, zoom }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!pdf || !containerWidth) return;
    let cancelled = false;
    let renderTask = null;

    (async () => {
      try {
        const page = await pdf.getPage(pageNumber);
        if (cancelled) return;
        // Baseline: fit the page to the container width; zoom scales from there.
        const base = page.getViewport({ scale: 1 });
        const cssScale = (containerWidth / base.width) * (zoom / 100);
        const dpr = window.devicePixelRatio || 1;
        const viewport = page.getViewport({ scale: cssScale * dpr });

        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `${Math.floor(viewport.width / dpr)}px`;
        canvas.style.height = `${Math.floor(viewport.height / dpr)}px`;

        renderTask = page.render({ canvasContext: ctx, viewport });
        await renderTask.promise;
      } catch {
        // RenderingCancelledException is expected on re-render/unmount — ignore.
      }
    })();

    return () => {
      cancelled = true;
      if (renderTask) { try { renderTask.cancel(); } catch { /* noop */ } }
    };
  }, [pdf, pageNumber, containerWidth, zoom]);

  return (
    <canvas ref={canvasRef} className="block mx-auto mb-4 bg-white rounded-lg shadow-2xl" />
  );
}

export default function PdfViewer({ src, zoom = 100 }) {
  const containerRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [error, setError] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Load the document whenever the source changes.
  useEffect(() => {
    if (!src) return;
    let cancelled = false;
    let loadingTask = null;
    let doc = null;
    setPdf(null);
    setNumPages(0);
    setError(null);

    (async () => {
      try {
        loadingTask = pdfjsLib.getDocument(src);
        doc = await loadingTask.promise;
        if (cancelled) { try { doc.destroy(); } catch { /* noop */ } return; }
        setPdf(doc);
        setNumPages(doc.numPages);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load PDF');
      }
    })();

    return () => {
      cancelled = true;
      if (doc) { try { doc.destroy(); } catch { /* noop */ } }
      if (loadingTask) { try { loadingTask.destroy(); } catch { /* noop */ } }
    };
  }, [src]);

  // Track container width for fit-to-width rendering (responsive to pane resizes).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setContainerWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="w-full">
      {error ? (
        <div className="bg-slate-800 rounded-lg p-8 text-center">
          <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">Couldn't render this PDF</p>
          <p className="text-slate-500 text-sm mt-2">{error}</p>
        </div>
      ) : !pdf ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Loading preview…
        </div>
      ) : (
        Array.from({ length: numPages }, (_, i) => (
          <PdfPage
            key={i}
            pdf={pdf}
            pageNumber={i + 1}
            containerWidth={containerWidth}
            zoom={zoom}
          />
        ))
      )}
    </div>
  );
}
